import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Sidebar from "../../components/layout/Sidebar";
import api from "../../utils/api";
import toast from "react-hot-toast";

const statusColor = {
  paid:     { bg: "#f0fff4", text: "#276749", dot: "#48bb78" },
  pending:  { bg: "#fffbeb", text: "#744210", dot: "#f6ad55" },
  overdue:  { bg: "#fff5f5", text: "#742a2a", dot: "#fc8181" },
  disputed: { bg: "#faf5ff", text: "#553c9a", dot: "#b794f4" },
  cancelled:{ bg: "#f7fafc", text: "#4a5568", dot: "#a0aec0" },
};

const fmt = (n) => "GHS " + Number(n || 0).toLocaleString("en-GH", { minimumFractionDigits: 2 });

const InvoiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [invoice,      setInvoice]      = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [status,       setStatus]       = useState("");
  const [notes,        setNotes]        = useState("");
  const [editingNotes, setEditingNotes] = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [deleting,     setDeleting]     = useState(false);
  const [allIds, setAllIds] = useState([]);
  const currentIndex = allIds.indexOf(id);

  useEffect(() => {
    const fetchInvoice = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/invoices/${id}`);
        setInvoice(res.data.invoice);
        setStatus(res.data.invoice.status);
        setNotes(res.data.invoice.notes || "");
      } catch (err) {
        toast.error("Invoice not found");
        navigate("/invoices");
      } finally {
        setLoading(false);
      }
    };
    fetchInvoice();
  }, [id, navigate]);

  useEffect(() => {
    const fetchIds = async () => {
      try {
        const res = await api.get("/invoices?limit=100");
        setAllIds(res.data.invoices.map(inv => inv._id));
      } catch (err) {}
   };
    fetchIds();
 },  []);

  const handleStatusChange = async (newStatus) => {
    setSaving(true);
    try {
      await api.put(`/invoices/${id}`, { status: newStatus });
      setStatus(newStatus);
      toast.success("Status updated");
    } catch (err) {
      toast.error("Failed to update status");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNotes = async () => {
    setSaving(true);
    try {
      await api.put(`/invoices/${id}`, { notes });
      setEditingNotes(false);
      toast.success("Notes saved");
    } catch (err) {
      toast.error("Failed to save notes");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this invoice? This cannot be undone.")) return;
    setDeleting(true);
    try {
      await api.delete(`/invoices/${id}`);
      toast.success("Invoice deleted");
      navigate("/invoices");
    } catch (err) {
      toast.error("Delete failed — admin access required");
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.shell}>
        <Sidebar />
        <main style={styles.main}>
          <div style={styles.loadingWrap}>Loading invoice...</div>
        </main>
      </div>
    );
  }

  if (!invoice) return null;

  const c = statusColor[status] || statusColor.pending;

  return (
    <div style={styles.shell}>
      <Sidebar />
      <main style={styles.main}>
        {/* Breadcrumb + actions */}
        <div style={styles.topBar}>
  <div style={styles.breadcrumb}>
    <Link to="/invoices" style={styles.breadLink}>← Invoices</Link>
    <span style={styles.breadSep}>/</span>
    <span style={styles.breadCurrent}>{invoice.invoiceNumber || "Invoice"}</span>
  </div>
  <div style={styles.actions}>
    {allIds.length > 0 && (
      <div style={styles.pageNav}>
        <button
          style={styles.pageNavBtn}
          disabled={currentIndex <= 0}
          onClick={() => navigate(`/invoices/${allIds[currentIndex - 1]}`)}
        >
          ← Prev
        </button>
        <span style={styles.pageNavCount}>
          {currentIndex + 1} / {allIds.length}
        </span>
        <button
          style={styles.pageNavBtn}
          disabled={currentIndex >= allIds.length - 1}
          onClick={() => navigate(`/invoices/${allIds[currentIndex + 1]}`)}
        >
          Next →
        </button>
      </div>
    )}
    {saving && <span style={styles.savingText}>Saving...</span>}
    {invoice.file?.url && (
      <a href={invoice.file.url} target="_blank" rel="noreferrer" style={styles.actionBtn}>⬇ Download</a>
    )}
    <button
      onClick={handleDelete}
      style={deleting ? { ...styles.actionBtn, ...styles.dangerBtn, opacity: 0.7 } : { ...styles.actionBtn, ...styles.dangerBtn }}
      disabled={deleting}
    >
      {deleting ? "Deleting..." : "🗑 Delete"}
    </button>
  </div>
</div>

        <div style={styles.layout}>
          {/* Left Column */}
          <div style={styles.leftCol}>
            {/* File Preview */}
            <div style={styles.card}>
              <div style={styles.cardTitle}>Invoice File</div>
              {invoice.file?.url ? (
                invoice.file.fileType === "pdf" ? (
                  <div style={styles.noPreview}>
                    <div style={styles.noPreviewIcon}>📄</div>
                    <div style={styles.noPreviewText}>{invoice.file.fileName}</div>
                    <div style={styles.noPreviewSub}>PDF — download to view</div>
                    <a href={invoice.file.url} target="_blank" rel="noreferrer" style={styles.downloadLink}>⬇ Download PDF</a>
                  </div>
                ) : (
                  <div style={styles.previewWrap}>
                    <img src={invoice.file.url} alt="invoice" style={styles.previewImg} />
                  </div>
                )
              ) : (
                <div style={styles.noPreview}>
                  <div style={styles.noPreviewIcon}>📄</div>
                  <div style={styles.noPreviewSub}>No file attached</div>
                </div>
              )}
              {invoice.file && (
                <div style={styles.fileMeta}>
                  <span style={styles.fileTag}>{invoice.file.fileType?.toUpperCase()}</span>
                  <span style={styles.fileSize}>{invoice.file.fileSize ? `${(invoice.file.fileSize / 1024).toFixed(0)} KB` : ""}</span>
                  <span style={styles.fileName}>{invoice.file.fileName}</span>
                </div>
              )}
            </div>

            {/* Notes */}
            <div style={styles.card}>
              <div style={styles.cardTitleRow}>
                <div style={styles.cardTitle}>Notes</div>
                {!editingNotes && (
                  <button onClick={() => setEditingNotes(true)} style={styles.editBtn}>Edit</button>
                )}
              </div>
              {editingNotes ? (
                <>
                  <textarea style={styles.textarea} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Add notes about this invoice..." rows={4} />
                  <div style={styles.noteActions}>
                    <button onClick={() => setEditingNotes(false)} style={styles.cancelBtn}>Cancel</button>
                    <button onClick={handleSaveNotes} style={styles.saveBtn} disabled={saving}>
                      {saving ? "Saving..." : "Save Notes"}
                    </button>
                  </div>
                </>
              ) : (
                <p style={styles.notesText}>{notes || "No notes added yet."}</p>
              )}
            </div>
          </div>

          {/* Right Column */}
          <div style={styles.rightCol}>
            {/* Header Card */}
            <div style={styles.card}>
              <div style={styles.invoiceHeader}>
                <div>
                  <div style={styles.invoiceNum}>{invoice.invoiceNumber || "—"}</div>
                  <div style={styles.invoiceTitle}>{invoice.title || "Untitled"}</div>
                </div>
                <span style={{ ...styles.badge, background: c.bg, color: c.text }}>
                  <span style={{ ...styles.badgeDot, background: c.dot }} />
                  {status}
                </span>
              </div>

              {/* Amount breakdown */}
              <div style={styles.amountBox}>
                <div style={styles.amountRow}>
                  <span style={styles.amountLabel}>Subtotal</span>
                  <span>{fmt(invoice.amount?.subtotal)}</span>
                </div>
                <div style={styles.amountRow}>
                  <span style={styles.amountLabel}>VAT ({invoice.vatRate || 15}%)</span>
                  <span>{fmt(invoice.amount?.vatAmount)}</span>
                </div>
                <div style={{ ...styles.amountRow, ...styles.amountTotal }}>
                  <span>Total</span>
                  <span>{fmt(invoice.amount?.totalAmount)}</span>
                </div>
              </div>

              {invoice.tags?.length > 0 && (
                <div style={styles.tagsRow}>
                  {invoice.tags.map(tag => <span key={tag} style={styles.tag}>#{tag}</span>)}
                </div>
              )}
            </div>

            {/* Details */}
            <div style={styles.card}>
              <div style={styles.cardTitle}>Details</div>
              <div style={styles.detailGrid}>
                {[
                  { label: "Type",         value: invoice.type },
                  { label: "Category",     value: invoice.category || "—" },
                  { label: "Fiscal Year",  value: invoice.fiscalYear || "—" },
                  { label: "Quarter",      value: invoice.fiscalQuarter || "—" },
                  { label: "Invoice Date", value: invoice.invoiceDate ? new Date(invoice.invoiceDate).toLocaleDateString("en-GH") : "—" },
                  { label: "Due Date",     value: invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString("en-GH") : "—" },
                  { label: "Payment Date", value: invoice.paymentDate ? new Date(invoice.paymentDate).toLocaleDateString("en-GH") : "—" },
                  { label: "VAT Invoice",  value: invoice.isVatInvoice ? "Yes" : "No" },
                  { label: "GRA Reported", value: invoice.graReported ? "Yes" : "Pending" },
                  { label: "Uploaded By",  value: invoice.uploadedBy?.name || "—" },
                ].map(row => (
                  <div key={row.label} style={styles.detailRow}>
                    <span style={styles.detailLabel}>{row.label}</span>
                    <span style={styles.detailValue}>{row.value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Vendor */}
            <div style={styles.card}>
              <div style={styles.cardTitle}>Vendor</div>
              <div style={styles.detailGrid}>
                {[
                  { label: "Name",  value: invoice.vendor?.name  || "—" },
                  { label: "TIN",   value: invoice.vendor?.tinNumber || "—" },
                  { label: "Email", value: invoice.vendor?.email || "—" },
                  { label: "Phone", value: invoice.vendor?.phone || "—" },
                ].map(row => (
                  <div key={row.label} style={styles.detailRow}>
                    <span style={styles.detailLabel}>{row.label}</span>
                    <span style={styles.detailValue}>{row.value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Update Status */}
            <div style={styles.card}>
              <div style={styles.cardTitle}>Update Status</div>
              <div style={styles.statusButtons}>
                {["pending","paid","overdue","disputed","cancelled"].map((s) => {
                  const sc = statusColor[s] || statusColor.pending;
                  return (
                    <button
                      key={s}
                      onClick={() => handleStatusChange(s)}
                      disabled={saving}
                      style={{
                        ...styles.statusBtn,
                        background: status === s ? sc.bg : "#f7f8fc",
                        color: status === s ? sc.text : "#718096",
                        border: status === s ? `1.5px solid ${sc.dot}` : "1.5px solid #e2e8f0",
                        fontWeight: status === s ? "bold" : "normal",
                      }}
                    >
                      <span style={{ ...styles.statusDot, background: sc.dot }} />
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

const styles = {
  shell: { display:"flex", minHeight:"100vh", background:"#f7f8fc", fontFamily:"sans-serif" },
  main: { flex:1, padding:"36px 40px", overflowY:"auto" },
  loadingWrap: { display:"flex", justifyContent:"center", alignItems:"center", height:"300px", color:"#a0aec0", fontSize:"14px" },
  topBar: { display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"28px" },
  breadcrumb: { display:"flex", alignItems:"center", gap:"8px" },
  breadLink: { fontSize:"14px", color:"#f6ad55", textDecoration:"none", fontWeight:"600" },
  breadSep: { color:"#a0aec0", fontSize:"14px" },
  breadCurrent: { fontSize:"14px", color:"#0a0f1e", fontWeight:"bold", fontFamily:"monospace" },
  actions: { display:"flex", alignItems:"center", gap:"10px" },
  savingText: { fontSize:"12px", color:"#a0aec0" },
  actionBtn: { padding:"8px 16px", background:"#fff", border:"1px solid #e2e8f0", borderRadius:"8px", fontSize:"13px", cursor:"pointer", color:"#2d3748", textDecoration:"none", fontWeight:"600" },
  dangerBtn: { color:"#fc8181", borderColor:"#fed7d7" },
  layout: { display:"grid", gridTemplateColumns:"1fr 1.4fr", gap:"20px", alignItems:"start" },
  leftCol: { display:"flex", flexDirection:"column", gap:"20px" },
  rightCol: { display:"flex", flexDirection:"column", gap:"20px" },
  card: { background:"#fff", borderRadius:"12px", border:"1px solid #e2e8f0", padding:"24px" },
  cardTitle: { fontSize:"13px", fontWeight:"bold", color:"#0a0f1e", marginBottom:"16px", textTransform:"uppercase", letterSpacing:"0.5px" },
  cardTitleRow: { display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"16px" },
  editBtn: { background:"none", border:"1px solid #e2e8f0", borderRadius:"6px", padding:"4px 10px", fontSize:"12px", cursor:"pointer", color:"#718096" },
  previewWrap: { borderRadius:"8px", overflow:"hidden", border:"1px solid #e2e8f0", marginBottom:"12px" },
  previewImg: { width:"100%", display:"block" },
  noPreview: { background:"#f7f8fc", borderRadius:"8px", padding:"32px", textAlign:"center", marginBottom:"12px" },
  noPreviewIcon: { fontSize:"36px", marginBottom:"10px" },
  noPreviewText: { fontSize:"13px", fontWeight:"600", color:"#0a0f1e", marginBottom:"4px" },
  noPreviewSub: { fontSize:"12px", color:"#a0aec0", marginBottom:"12px" },
  downloadLink: { fontSize:"13px", color:"#f6ad55", fontWeight:"bold", textDecoration:"none" },
  fileMeta: { display:"flex", alignItems:"center", gap:"8px" },
  fileTag: { background:"#ebf8ff", color:"#2b6cb0", padding:"3px 8px", borderRadius:"4px", fontSize:"10px", fontWeight:"bold", fontFamily:"monospace" },
  fileSize: { fontSize:"12px", color:"#a0aec0" },
  fileName: { fontSize:"12px", color:"#718096" },
  textarea: { width:"100%", border:"1.5px solid #e2e8f0", borderRadius:"8px", padding:"10px 12px", fontSize:"13px", fontFamily:"sans-serif", resize:"vertical", outline:"none", boxSizing:"border-box" },
  noteActions: { display:"flex", justifyContent:"flex-end", gap:"8px", marginTop:"10px" },
  cancelBtn: { padding:"7px 14px", background:"#fff", border:"1px solid #e2e8f0", borderRadius:"6px", fontSize:"12px", cursor:"pointer", color:"#718096" },
  saveBtn: { padding:"7px 14px", background:"#0a0f1e", color:"#fff", border:"none", borderRadius:"6px", fontSize:"12px", cursor:"pointer", fontWeight:"bold" },
  notesText: { fontSize:"13px", color:"#4a5568", lineHeight:1.6, margin:0 },
  invoiceHeader: { display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:"20px" },
  invoiceNum: { fontSize:"20px", fontWeight:"bold", color:"#0a0f1e", fontFamily:"monospace", marginBottom:"4px" },
  invoiceTitle: { fontSize:"13px", color:"#718096" },
  badge: { display:"inline-flex", alignItems:"center", gap:"5px", padding:"5px 12px", borderRadius:"20px", fontSize:"12px", fontWeight:"600", textTransform:"capitalize", flexShrink:0 },
  badgeDot: { width:"6px", height:"6px", borderRadius:"50%" },
  amountBox: { background:"#f7f8fc", borderRadius:"8px", padding:"16px", marginBottom:"16px" },
  amountRow: { display:"flex", justifyContent:"space-between", fontSize:"13px", color:"#4a5568", marginBottom:"8px" },
  amountLabel: { color:"#718096" },
  amountTotal: { borderTop:"1px solid #e2e8f0", paddingTop:"10px", marginTop:"4px", fontWeight:"bold", fontSize:"15px", color:"#0a0f1e", marginBottom:0 },
  tagsRow: { display:"flex", flexWrap:"wrap", gap:"6px" },
  tag: { background:"#edf2f7", color:"#4a5568", padding:"3px 10px", borderRadius:"20px", fontSize:"11px" },
  detailGrid: { display:"flex", flexDirection:"column", gap:"10px" },
  detailRow: { display:"flex", justifyContent:"space-between", alignItems:"center", fontSize:"13px", borderBottom:"1px solid #f0f0f0", paddingBottom:"8px" },
  detailLabel: { color:"#a0aec0", fontSize:"12px" },
  detailValue: { color:"#0a0f1e", fontWeight:"500", textTransform:"capitalize" },
  statusButtons: { display:"flex", flexWrap:"wrap", gap:"8px" },
  statusBtn: { padding:"7px 14px", borderRadius:"20px", fontSize:"12px", cursor:"pointer", display:"flex", alignItems:"center", gap:"6px", textTransform:"capitalize" },
  statusDot: { width:"6px", height:"6px", borderRadius:"50%" },
  pageNav: { display:"flex", alignItems:"center", gap:"6px" },
  pageNavBtn: { padding:"6px 12px", background:"#fff", border:"1px solid #e2e8f0", borderRadius:"6px", fontSize:"12px", cursor:"pointer", color:"#4a5568" },
  pageNavCount: { fontSize:"12px", color:"#a0aec0", padding:"0 4px" },
};

export default InvoiceDetail;